
import React, { Component, ErrorInfo, ReactNode } from "react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { normalizeError } from "@/hooks/useErrorHandler";
import { useThemeConfig } from "@/components/ui/theme-config-provider";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState { hasError: boolean; error: Error | null }

function ThemedFallback({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  const { theme } = useThemeConfig();
  return (
    <div
      className="p-4 flex flex-col items-center justify-center min-h-[200px]"
      style={{ backgroundColor: theme.backgroundColor, color: theme.textColor }}
    >
      <Alert variant="destructive" className="mb-4 w-full max-w-md">
        <AlertTitle>Qualcosa è andato storto</AlertTitle>
        <AlertDescription>{message}</AlertDescription>
      </Alert>
      <Button
        onClick={onRetry}
        variant="outline"
        className="mt-2"
        style={{ color: theme.textColor, borderColor: theme.borderColor }}
      >
        <RefreshCw className="mr-2 h-4 w-4" /> Riprova
      </Button>
    </div>
  );
}

/**
 * Error boundary che evita crash mostrando un fallback coerente con il tema.
 * Scelta UX: offrire un bottone di retry per ripristinare rapidamente la vista.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("ErrorBoundary caught:", error, info);
    this.props.onError?.(error, info);
  }

  reset = (): void => this.setState({ hasError: false, error: null });

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    const message =
      normalizeError(this.state.error).message ||
      "Si è verificato un errore imprevisto";
    return <ThemedFallback message={message} onRetry={this.reset} />;
  }
}

export default ErrorBoundary;
